import { Tooltip, mergeClasses } from "@fluentui/react-components";
import {
  Delete20Regular,
  Edit20Regular,
  Eye20Regular,
} from "@fluentui/react-icons";
import { updateCategory } from "api";
import { ConstantValues } from "common/constants";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import { AdminRowAction } from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { findSidebarItem } from "components/adminSidebar/adminSidebarConfig";
import { useAdminListFilters } from "hooks/useAdminListFilters";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { useAdminListSearch } from "hooks/useAdminListSearch";
import { useAdminListSort } from "hooks/useAdminListSort";
import { ChevronIcon } from "icons/ChevronIcon";
import { ExportIcon } from "icons/ExportIcon";
import { PlusIcon } from "icons/PlusIcon";
import { SearchIcon } from "icons/SearchIcon";
import { ICategory } from "models/ICategory";
import { ITableColumn } from "models/ITableColumn";
import { ITag } from "models/ITag";
import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { RootState } from "store";
import {
  useSharedAdminContentHeaderStyles,
  useSharedButtonStyles,
  useSharedAdminCellStyles,
} from "theme/sharedStyles";
import {
  formatDate,
  generateCSV,
  downloadCSV,
  userIsAdminOrPlatformAdmin,
} from "utils/generalUtils";
import { EditTag } from "./EditDialog/EditTag";
import { ViewTag } from "./ViewDialog/ViewTag";
import { useManageTagsStyles } from "./ManageTags.styles";
import { IManageTagsProps } from "./ManageTags.types";

export const ManageTags = (props: IManageTagsProps): JSX.Element => {
  const { isLoading: isLoadingProp = false } = props;
  const cellStyles = useManageTagsStyles();
  const adminCellStyles = useSharedAdminCellStyles();
  const colStyles = useAdminColumnStyles();
  const headerClasses = useSharedAdminContentHeaderStyles();
  const buttonStyles = useSharedButtonStyles();
  const currentUser: any = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );

  const [viewingTag, setViewingTag] = useState<ITag | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState<boolean>(false);
  const [editingTag, setEditingTag] = useState<ITag | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [deletingTag, setDeletingTag] = useState<ITag | null>(null);
  const { filters, setFilter } = useAdminListFilters({
    cacheKey: "Tags",
    filterKeys: ["category", "status"] as const,
  });
  const queryClient = useQueryClient();

  const tags: ITag[] = useMemo(
    () => getTagsFromCategories(props.categories),
    [props.categories],
  );

  const columns: ITableColumn<ITag>[] = useMemo(() => {
    const baseColumns: ITableColumn<ITag>[] = [
      {
        columnId: "name",
        displayValue: "Title",
        className: colStyles.normal,
        renderHeaderCell: () => "Title",
        compare: (a: ITag, b: ITag) => a.name.localeCompare(b.name),
        renderCell: (tag: ITag) => (
          <Tooltip content={tag.name} relationship="label">
            <span
              className={mergeClasses(
                adminCellStyles.nameText,
                cellStyles.nameTextExtra,
              )}
            >
              {tag.name}
            </span>
          </Tooltip>
        ),
      },
      {
        columnId: "category",
        displayValue: "Category",
        className: colStyles.normal,
        renderHeaderCell: () => "Category",
        compare: (a: ITag, b: ITag) =>
          (a.category?.title || ConstantValues.EMPTY_STRING).localeCompare(
            b.category?.title || ConstantValues.EMPTY_STRING,
          ),
        renderCell: (tag: ITag) => <>{tag.category?.title || "No Category"}</>,
      },
      {
        columnId: "updatedBy",
        displayValue: "Updated By",
        className: colStyles.normal,
        renderHeaderCell: () => "Updated By",
        compare: (a: ITag, b: ITag) =>
          (
            a.updatedBy ||
            a.displayName ||
            ConstantValues.EMPTY_STRING
          ).localeCompare(
            b.updatedBy || b.displayName || ConstantValues.EMPTY_STRING,
          ),
        renderCell: (tag: ITag) => (
          <>{tag.updatedBy || tag.displayName || "Unknown"}</>
        ),
      },
      {
        columnId: "isPublic",
        displayValue: "Is Public",
        className: colStyles.normal,
        renderHeaderCell: () => "Is Public",
        compare: (a: ITag, b: ITag) => Number(a.isPublic) - Number(b.isPublic),
        renderCell: (tag: ITag) => <>{tag.isPublic ? "Yes" : "No"}</>,
      },
      {
        columnId: "updatedAt",
        displayValue: "Last Updated",
        className: colStyles.normal,
        renderHeaderCell: () => "Last Updated",
        compare: (a: ITag, b: ITag) => a.updatedAt - b.updatedAt,
        renderCell: (tag: ITag) => <>{formatDate(tag.updatedAt)}</>,
      },
    ];

    return baseColumns;
  }, [cellStyles, colStyles]);

  const {
    sortColumn,
    isSortDescending,
    sortItems,
    onSortColumnChanged,
    onClearColumnChanged,
  } = useAdminListSort<ITag>({
    cacheKey: "Tags",
    defaultSortColumn: "name",
    defaultDescending: false,
  });

  const searchFields = useMemo(() => ["name"] as (keyof ITag)[], []);

  const {
    searchQuery: internalSearchQuery,
    handleSearchChange,
    handleSearchValueChange,
    filteredItems,
  } = useAdminListSearch<ITag>({
    items: tags,
    searchFields,
  });

  const fullyFilteredItems = useMemo(() => {
    let result = filteredItems;
    if (filters.category) {
      result = result.filter((tag) => tag.category?.id === filters.category);
    }
    if (filters.status) {
      result = result.filter((tag) =>
        filters.status === "public"
          ? tag.isPublic === true
          : tag.isPublic !== true,
      );
    }
    return result;
  }, [filteredItems, filters.category, filters.status]);

  const sortedItems = useMemo(
    () => sortItems(fullyFilteredItems, columns),
    [fullyFilteredItems, sortItems, columns],
  );

  const emptyStateMessage = useMemo(() => {
    const hasActiveFilters =
      internalSearchQuery || filters.category || filters.status;
    return hasActiveFilters
      ? "No knowledge components match the current filters."
      : 'No knowledge components found. Use "Add New" to create one.';
  }, [internalSearchQuery, filters.category, filters.status]);

  const {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount,
    itemsPerPage,
  } = useAdminListPagination<ITag>({
    items: sortedItems,
    itemsPerPage: 10,
  });

  function getTagsFromCategories(categories: ICategory[]): ITag[] {
    return categories.reduce((acc: ITag[], category: ICategory) => {
      if (category.tags && category.tags.length > 0) {
        const tagsWithCategory = category.tags.map((tag: ITag) => ({
          ...tag,
          category,
        }));
        return [...acc, ...tagsWithCategory];
      }
      return acc;
    }, [] as ITag[]);
  }

  const handleViewTag = useCallback((tag: ITag): void => {
    setViewingTag(tag);
    setIsViewDialogOpen(true);
  }, []);

  const isTagDisabledForCurrentUser = useCallback(
    (tag: ITag): boolean => {
      return (
        !userIsAdminOrPlatformAdmin(currentUser) &&
        tag.userId !== currentUser.id.toString()
      );
    },
    [currentUser],
  );

  const handleEditTag = useCallback(
    (tag: ITag): void => {
      if (isTagDisabledForCurrentUser(tag)) {
        return;
      }
      setEditingTag(tag);
      setIsEditDialogOpen(true);
    },
    [isTagDisabledForCurrentUser],
  );

  const handleDeleteTag = useCallback(
    (tag: ITag): void => {
      if (isTagDisabledForCurrentUser(tag)) {
        return;
      }
      setDeletingTag(tag);
      setIsDeleteDialogOpen(true);
    },
    [isTagDisabledForCurrentUser],
  );

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deletingTag) {
      return;
    }

    setIsSaving(true);

    try {
      const categoryContainingTag = props.categories.find((category) =>
        category.tags.some((tag) => tag.id === deletingTag.id),
      );

      if (!categoryContainingTag) {
        return;
      }

      const updatedCategory: ICategory = {
        ...categoryContainingTag,
        tags: categoryContainingTag.tags.filter(
          (tag) => tag.id !== deletingTag.id,
        ),
      };

      await updateCategory({ category: updatedCategory });
      await queryClient.invalidateQueries("categories");
    } catch (error) {
      console.error("Failed to delete knowledge component:", error);
    } finally {
      setIsSaving(false);
      setIsDeleteDialogOpen(false);
      setDeletingTag(null);
    }
  };

  const handleCloseEditDialog = (): void => {
    setIsEditDialogOpen(false);
    setEditingTag(null);
  };

  const handleEditSuccess = async (): Promise<void> => {
    setIsSaving(true);
    try {
      await queryClient.invalidateQueries("categories");
    } finally {
      setIsSaving(false);
    }
  };

  const rowActions: AdminRowAction<ITag>[] = useMemo(
    () => [
      {
        icon: <Eye20Regular />,
        tooltip: "View",
        onClick: handleViewTag,
      },
      {
        icon: <Edit20Regular />,
        tooltip: "Edit",
        onClick: handleEditTag,
        disabled: isTagDisabledForCurrentUser,
      },
      {
        icon: <Delete20Regular />,
        tooltip: "Delete",
        onClick: handleDeleteTag,
        disabled: isTagDisabledForCurrentUser,
      },
    ],
    [
      handleViewTag,
      handleEditTag,
      handleDeleteTag,
      isTagDisabledForCurrentUser,
    ],
  );

  const handleExport = useCallback((): void => {
    const csv = generateCSV(fullyFilteredItems, [
      {
        header: "Title",
        accessor: (tag) => tag.name || ConstantValues.EMPTY_STRING,
      },
      {
        header: "Category",
        accessor: (tag) => tag.category?.title || ConstantValues.EMPTY_STRING,
      },
      {
        header: "Updated By",
        accessor: (tag) =>
          tag.updatedBy || tag.displayName || ConstantValues.EMPTY_STRING,
      },
      { header: "Is Public", accessor: (tag) => (tag.isPublic ? "Yes" : "No") },
      { header: "Last Updated", accessor: (tag) => formatDate(tag.updatedAt) },
    ]);
    downloadCSV(csv, "knowledge-components.csv");
  }, [fullyFilteredItems]);

  const categoryOptions = useMemo(
    () =>
      (props.categories ?? []).map((cat) => ({ key: cat.id, text: cat.title })),
    [props.categories],
  );

  return (
    <>
      <div className={headerClasses.root}>
        <div
          className={mergeClasses(
            headerClasses.titleRow,
            headerClasses.titleRowWithFilters,
          )}
        >
          <h1 className={headerClasses.title}>
            {findSidebarItem("tags")?.label ?? "Knowledge Components"}
          </h1>
          <div className={headerClasses.actions}>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.secondaryButton,
              )}
              onClick={handleExport}
              type="button"
            >
              <ExportIcon /> Export
            </button>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.primaryButton,
              )}
              onClick={() => {
                setEditingTag(null);
                setIsEditDialogOpen(true);
              }}
              type="button"
            >
              <PlusIcon /> Add New
            </button>
          </div>
        </div>
        <div className={headerClasses.filterRow}>
          <div className={headerClasses.searchInputWrapper}>
            <SearchIcon className={headerClasses.searchIcon} />
            <input
              type="text"
              className={headerClasses.searchInput}
              placeholder="Search knowledge components..."
              value={internalSearchQuery}
              onChange={(e) => handleSearchValueChange(e.target.value)}
              aria-label="Search knowledge components..."
            />
          </div>
          <div className={headerClasses.filterSelectWrapper}>
            <select
              className={headerClasses.filterSelect}
              value={filters.category}
              onChange={(e) => setFilter("category", e.target.value)}
              aria-label="All Categories"
            >
              <option value="">All Categories</option>
              {categoryOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.text}
                </option>
              ))}
            </select>
            <ChevronIcon className={headerClasses.filterChevron} />
          </div>
          <div className={headerClasses.filterSelectWrapper}>
            <select
              className={headerClasses.filterSelect}
              value={filters.status}
              onChange={(e) => setFilter("status", e.target.value)}
              aria-label="All Status"
            >
              <option value="">All Status</option>
              <option value="public">Public</option>
              <option value="notPublic">Not Public</option>
            </select>
            <ChevronIcon className={headerClasses.filterChevron} />
          </div>
        </div>
      </div>
      <AdminListPage<ITag>
        title="Tags"
        items={tags}
        columns={columns}
        isLoading={isLoadingProp || isSaving}
        headerActions={[]}
        searchPlaceholder="Search knowledge components..."
        searchQuery={internalSearchQuery}
        onSearchChange={handleSearchChange}
        hideHeader={true}
        rowActions={rowActions}
        getRowId={(tag) => tag.id ?? ConstantValues.EMPTY_STRING}
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortColumnChanged}
        onSortClear={onClearColumnChanged}
        paginatedItems={paginatedItems}
        selectedPageIndex={selectedPageIndex}
        pageCount={pageCount}
        totalItemsCount={totalItemsCount}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        emptyStateMessage={emptyStateMessage}
      />

      {isViewDialogOpen && viewingTag && (
        <ViewTag
          isOpen={isViewDialogOpen}
          onDismiss={() => {
            setIsViewDialogOpen(false);
            setViewingTag(null);
          }}
          tag={viewingTag}
        />
      )}

      {isEditDialogOpen && (
        <EditTag
          isOpen={isEditDialogOpen}
          onDismiss={(isSaved: boolean): void => {
            if (isSaved) {
              handleEditSuccess();
            }
            handleCloseEditDialog();
          }}
          tags={tags}
          tag={editingTag}
          categories={props.categories}
          onTagUpdate={handleEditSuccess}
        />
      )}

      <ActionDialog
        isVisible={isDeleteDialogOpen}
        title="Delete Knowledge Component"
        message={`Are you sure you want to permanently delete "${deletingTag?.name}"? This action cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleConfirmDelete}
        onDismiss={() => {
          setIsDeleteDialogOpen(false);
          setDeletingTag(null);
        }}
      />
    </>
  );
};
